/**
 * POS cart write funnel (sales register). Adds a new side effect:
 * 1) Add/adjust `cartBatchOp` builders in CartService.js and send via `applyCartBatch`.
 * 2) Add Laravel `CartBatchRequest` validation + `CartService::applyBatch` branch.
 * 3) Extend this coordinator to merge the op into `executeCartFlush` / `applyImmediateOps` as needed.
 * 4) Update API_CONTRACT.md (backend + frontend).
 *
 * Tab payment preload keeps using `cartStore.setCartCustomerId` + HTTP POST /customer.
 */

import { Notify } from 'quasar'
import { i18n } from '@/boot/i18n'
import { cartBatchOp, newCartBatchIdempotencyKey } from './CartService'
import { productService } from './ProductService'
import { useCartStore } from '@/stores/cart'
import { useLocationStore } from '@/stores/location'
import { metadataSignatureForMerge } from '@/utils/cartLineIdentity'
import { isRingByAmountLine } from '@/utils/ringByAmount'
import { registerProductCartMetadata } from '@/utils/registerProductBehavior'

const CART_FLUSH_MS = 120
const DISCOUNT_ONLY_FLUSH_MS = 400

let boundFlushPosCartWrites = async () => {}
let boundScheduleOrderDiscountFromUi = () => {}
let boundClearOrderDiscountOnServer = async () => {}
let boundApplyImmediateOps = async () => {}
let boundEnqueueSetCustomerAndFlush = async () => {}

/**
 * Flush debounced POS cart writes (safe before suspend/payment from code that runs earlier in setup).
 */
export async function flushPosCartWritesNow () {
  return boundFlushPosCartWrites()
}

export function schedulePosCartOrderDiscountFromUi (typeUi, amount) {
  boundScheduleOrderDiscountFromUi(typeUi, amount)
}

export async function clearPosCartOrderDiscountOnServer () {
  return boundClearOrderDiscountOnServer()
}

export async function applyImmediatePosCartOps (ops) {
  return boundApplyImmediateOps(ops)
}

export async function enqueuePosCartSetCustomerAndFlush (customerId) {
  return boundEnqueueSetCustomerAndFlush(customerId)
}

/**
 * Single debounced write funnel for the sales register: line ops, order discount, and customer
 * coalesce into one POST /cart/batch per flush where possible.
 *
 * @param {{ onCartChanged?: () => void }} options
 */
export function createPosCartWriteCoordinator (options = {}) {
  const { onCartChanged } = options
  const cartStore = useCartStore()
  const locationStore = useLocationStore()

  const notifyCartChanged = () => {
    if (typeof onCartChanged === 'function') {
      try {
        onCartChanged()
      } catch (e) {
        console.warn('onCartChanged callback failed', e)
      }
    }
  }

  const translate = (key, params) => i18n.global.t(key, params)

  const resolveProductLabel = (product, displayName) => {
    const fromDisplay = typeof displayName === 'string' ? displayName.trim() : ''
    if (fromDisplay) return fromDisplay

    const fromProduct = typeof product?.name === 'string' ? product.name.trim() : ''
    if (fromProduct) return fromProduct

    return translate('sales.inventoryOversoldUnknownProduct')
  }

  let lastCartSnapshot = null
  const desiredLineQuantities = {}

  const pendingAddOps = new Map()
  /** @type {Map<number, { remove?: boolean, quantity?: number, checkStock?: boolean }>} */
  const pendingQtyOps = new Map()
  let cartFlushTimer = null
  let discountOnlyTimer = null
  let cartBatchDeferred = null
  let cartFlushChain = Promise.resolve()

  /** @type {undefined | null | number} undefined = no pending customer op */
  let pendingCustomer = undefined
  let discountDirty = false
  let pendingDiscountUi = { type: '$', amount: 0 }

  const metadataForApi = (meta) => {
    if (!meta || typeof meta !== 'object') return {}
    const rest = { ...meta }
    delete rest.__registerCartPendingAdd
    return rest
  }

  const ensureCartBatchDeferred = () => {
    if (!cartBatchDeferred) {
      let resolve
      let reject
      const promise = new Promise((res, rej) => {
        resolve = res
        reject = rej
      })
      cartBatchDeferred = { promise, resolve, reject }
    }
    return cartBatchDeferred
  }

  const snapshotCart = () => {
    lastCartSnapshot = (cartStore.items || []).map(item => ({
      ...item,
      metadata: { ...(item.metadata || {}) }
    }))
  }

  const restoreCartSnapshot = () => {
    if (lastCartSnapshot) {
      cartStore.items = lastCartSnapshot
    }
  }

  const notifyError = (message) => {
    Notify.create({
      type: 'negative',
      message
    })
  }

  const notifyStockWarning = (message) => {
    Notify.create({
      type: 'warning',
      message,
      timeout: 4500
    })
  }

  const cancelTimers = () => {
    if (cartFlushTimer) {
      clearTimeout(cartFlushTimer)
      cartFlushTimer = null
    }
    if (discountOnlyTimer) {
      clearTimeout(discountOnlyTimer)
      discountOnlyTimer = null
    }
  }

  const executeCartFlush = async () => {
    const deferred = cartBatchDeferred
    cartBatchDeferred = null

    const addEntries = Array.from(pendingAddOps.entries())
    pendingAddOps.clear()
    const qtyEntries = Array.from(pendingQtyOps.entries())
    pendingQtyOps.clear()

    const hasLineWork = addEntries.length > 0 || qtyEntries.length > 0
    const customerOp = pendingCustomer
    const hasCustomerWork = customerOp !== undefined
    const hasDiscountWork = discountDirty
    if (!hasLineWork && !hasCustomerWork && !hasDiscountWork) {
      deferred?.resolve?.()
      return
    }

    try {
      if (hasLineWork) {
        snapshotCart()
      }

      for (const [_lineId, qop] of qtyEntries) {
        if (qop.remove || !qop.checkStock || !qop.quantity || qop.quantity <= 0) continue
        const line = (cartStore.items || []).find(i => i.line_item_id === _lineId)
        if (!line) continue
        const productId = line.product_id ?? line.metadata?.product_id
        if (
          line.metadata?.redeeming_for_points ||
          line.metadata?.non_stock ||
          !productId
        ) {
          continue
        }
        const locationId = locationStore.getCurrentLocation?.id
        const stockCheck = await productService.checkStock(productId, locationId)
        if (stockCheck?.warning?.message) {
          Notify.create({
            type: 'warning',
            message: stockCheck.warning.message
          })
        }
        const available = stockCheck?.available
        if (typeof available === 'number' && available < qop.quantity) {
          notifyStockWarning(
            translate('sales.posLowStockQuantityWarning', { available })
          )
        }
      }

      const ops = []

      for (const [, op] of addEntries) {
        ops.push({
          op: 'add_item',
          payload: {
            product_id: op.product.id ?? null,
            sku: op.product.barcode || String(op.product.id),
            name: op.product.name,
            price: Number(op.product.price),
            quantity: op.quantity,
            metadata: metadataForApi(op.incomingMetadata)
          }
        })
      }

      const removes = []
      const sets = []
      for (const [lineId, qop] of qtyEntries) {
        if (qop.remove) {
          removes.push(lineId)
        } else if (typeof qop.quantity === 'number') {
          sets.push([lineId, qop.quantity, qop])
        }
      }
      removes.sort((a, b) => a - b)
      sets.sort((a, b) => a[0] - b[0])

      for (const lineId of removes) {
        ops.push(cartBatchOp.removeItem(lineId))
      }
      for (const [lineId, qty, qop] of sets) {
        const metadata = qop?.metadata
        const price = qop?.price
        ops.push(cartBatchOp.setQuantity(lineId, qty, metadata, price))
      }

      if (hasCustomerWork) {
        ops.push(cartBatchOp.setCustomer(customerOp))
        pendingCustomer = undefined
      }

      if (hasDiscountWork) {
        ops.push(cartBatchOp.setOrderDiscountFromSaleUi(pendingDiscountUi.type, pendingDiscountUi.amount))
        discountDirty = false
      }

      const cartData = await cartStore.applyCartBatch({ ops }, newCartBatchIdempotencyKey())
      if (cartData.concurrent_cart_activity) {
        Notify.create({
          type: 'info',
          message: translate('sales.cartConcurrentActivity')
        })
      }
      notifyCartChanged()
      deferred?.resolve?.()
    } catch (e) {
      console.error('Error flushing cart batch:', e)
      try {
        await cartStore.fetchCart()
      } catch (fetchErr) {
        console.error('Failed to resync cart after batch error', fetchErr)
        restoreCartSnapshot()
      }
      const msg =
        addEntries.length > 0 && qtyEntries.length === 0 && !hasCustomerWork && !hasDiscountWork
          ? 'Failed to add item to cart'
          : 'Failed to update cart'
      notifyError(msg)
      deferred?.reject?.(e)
    }
  }

  const scheduleCartFlush = () => {
    if (discountOnlyTimer) {
      clearTimeout(discountOnlyTimer)
      discountOnlyTimer = null
    }
    if (cartFlushTimer) clearTimeout(cartFlushTimer)
    cartFlushTimer = setTimeout(() => {
      cartFlushTimer = null
      cartFlushChain = cartFlushChain.then(() => executeCartFlush())
    }, CART_FLUSH_MS)
  }

  const scheduleDiscountOnlyFlush = () => {
    if (discountOnlyTimer) clearTimeout(discountOnlyTimer)
    discountOnlyTimer = setTimeout(() => {
      discountOnlyTimer = null
      cartFlushChain = cartFlushChain.then(() => executeCartFlush())
    }, DISCOUNT_ONLY_FLUSH_MS)
  }

  const markDiscountDirtyFromUi = (typeUi, amount) => {
    pendingDiscountUi = { type: typeUi, amount: Number(amount) || 0 }
    discountDirty = true
    const lineBusy = pendingAddOps.size > 0 || pendingQtyOps.size > 0
    if (lineBusy) {
      scheduleCartFlush()
    } else {
      scheduleDiscountOnlyFlush()
    }
  }

  /**
   * Wait until queued batches finish (runs a flush if timers were pending).
   */
  const flushNow = async () => {
    cancelTimers()
    await cartFlushChain
    const p = executeCartFlush()
    cartFlushChain = p.catch(() => {})
    await p
    await cartFlushChain
  }

  const whenIdle = () => flushNow()

  const snapshotRevisionForSuspend = () => cartStore.cartRevision

  /**
   * @param {Array<{ op: string, payload?: object }>} ops
   */
  const applyImmediateOps = async (ops) => {
    await flushNow()
    const cartData = await cartStore.applyCartBatch({ ops }, newCartBatchIdempotencyKey())
    if (cartData.concurrent_cart_activity) {
      Notify.create({
        type: 'info',
        message: translate('sales.cartConcurrentActivity')
      })
    }
    notifyCartChanged()
    return cartData
  }

  const enqueueSetCustomerAndFlush = async (customerId) => {
    pendingCustomer = customerId == null ? null : customerId
    await flushNow()
  }

  const clearOrderDiscountOnServer = async () => {
    pendingDiscountUi = { type: '$', amount: 0 }
    discountDirty = true
    await flushNow()
  }

  const addProductToCart = async (product, { quantity = 1, extraMetadata = {}, displayName = null } = {}) => {
    const productLabel = resolveProductLabel(product, displayName)

    if (!product.non_stock && typeof product.stock === 'number' && product.stock < quantity) {
      notifyStockWarning(translate('sales.posLowStockAddWarning', { product: productLabel }))
    }

    const lineName = productLabel

    const incomingMetadata = {
      discount: { type: '$', amount: 0 },
      barcode: product.barcode,
      ...registerProductCartMetadata(product),
      ...extraMetadata
    }
    const sku = product.barcode || product.id.toString()
    const accumulatorKey = incomingMetadata.open_price
      ? `${sku}\0open_price\0${Date.now()}\0${Math.random()}`
      : `${sku}\0${metadataSignatureForMerge(incomingMetadata)}`

    const items = cartStore.items || []
    const incomingSig = metadataSignatureForMerge(incomingMetadata)
    const existing = incomingMetadata.open_price
      ? null
      : items.find(item =>
        item.sku === sku && metadataSignatureForMerge(item.metadata || {}) === incomingSig
      )

    if (existing) {
      existing.quantity = Number(existing.quantity || 0) + Number(quantity)
    } else {
      const nextId = items.reduce((max, item) => Math.max(max, item.line_item_id || 0), 0) + 1
      items.push({
        line_item_id: nextId,
        product_id: product.id ?? null,
        sku,
        name: lineName,
        price: product.price,
        quantity,
        metadata: {
          ...incomingMetadata,
          __registerCartPendingAdd: true
        }
      })
    }

    notifyCartChanged()

    const prevOp = pendingAddOps.get(accumulatorKey)
    pendingAddOps.set(accumulatorKey, {
      product,
      quantity: (prevOp?.quantity || 0) + quantity,
      incomingMetadata
    })

    const { promise } = ensureCartBatchDeferred()
    scheduleCartFlush()

    try {
      await promise
    } catch (error) {
      console.error('Error adding product to cart:', error)
      throw error
    }
  }

  const addRedemptionItemToCart = async (cartItem) => {
    await addProductToCart(cartItem, {
      quantity: cartItem.quantity || 1,
      extraMetadata: {
        redeeming_for_points: true,
        redemption_cost: cartItem.redemption_cost,
        total_redemption_cost: cartItem.total_redemption_cost,
        redemption_notes: cartItem.redemption_notes
      }
    })
  }

  const setLineItemQuantity = async (item, newQuantity, { checkStock = false, metadata, price } = {}) => {
    const lineItemId = item.line_item_id
    if (lineItemId == null) {
      console.warn('Cannot update quantity for item without line_item_id', item)
      return
    }

    const targetQuantity = Math.max(0, Number.isFinite(newQuantity) ? newQuantity : 0)

    item.quantity = targetQuantity
    if (typeof price === 'number') {
      item.price = price
    }
    if (metadata !== undefined) {
      item.metadata = metadata
    }

    desiredLineQuantities[lineItemId] = targetQuantity

    pendingQtyOps.set(lineItemId, {
      quantity: targetQuantity,
      checkStock,
      ...(metadata !== undefined ? { metadata } : {}),
      ...(typeof price === 'number' ? { price } : {})
    })

    const { promise } = ensureCartBatchDeferred()
    scheduleCartFlush()

    try {
      await promise
    } catch (error) {
      console.error('Error setting line item quantity:', error)
      throw error
    }
  }

  const updateCartLine = async (item, { quantity, price, metadata } = {}) => {
    const lineItemId = item.line_item_id
    if (lineItemId == null) {
      console.warn('Cannot update cart line without line_item_id', item)
      return
    }

    if (typeof quantity === 'number' && quantity <= 0) {
      await removeLineItem(item)
      return
    }

    const qty = typeof quantity === 'number'
      ? quantity
      : Math.max(0, Number(item.quantity) || 0)

    await setLineItemQuantity(item, qty, {
      checkStock: false,
      ...(typeof price === 'number' ? { price } : {}),
      ...(metadata !== undefined ? { metadata } : {})
    })
  }

  const incrementLineQuantity = async (item) => {
    const nextQuantity = (item.quantity || 0) + 1
    await setLineItemQuantity(item, nextQuantity, { checkStock: false })
  }

  const decrementLineQuantity = async (item) => {
    if (isRingByAmountLine(item)) {
      await removeLineItem(item)
      return
    }

    const nextQuantity = (item.quantity || 0) - 1
    if (nextQuantity <= 0) {
      await removeLineItem(item)
      return
    }

    await setLineItemQuantity(item, nextQuantity, { checkStock: false })
  }

  const removeLineItem = async (item) => {
    const lineItemId = item.line_item_id
    if (lineItemId == null) {
      console.warn('Cannot remove item without line_item_id', item)
      return
    }

    delete desiredLineQuantities[lineItemId]

    const currentItems = cartStore.items || []
    const index = currentItems.findIndex(i => i.line_item_id === lineItemId)
    if (index > -1) {
      currentItems.splice(index, 1)
    }

    pendingQtyOps.set(lineItemId, { remove: true })

    notifyCartChanged()

    const { promise } = ensureCartBatchDeferred()
    scheduleCartFlush()

    try {
      await promise
    } catch (error) {
      console.error('Error removing line item:', error)
      throw error
    }
  }

  boundFlushPosCartWrites = flushNow
  boundScheduleOrderDiscountFromUi = markDiscountDirtyFromUi
  boundClearOrderDiscountOnServer = clearOrderDiscountOnServer
  boundApplyImmediateOps = applyImmediateOps
  boundEnqueueSetCustomerAndFlush = enqueueSetCustomerAndFlush

  return {
    addProductToCart,
    addRedemptionItemToCart,
    setLineItemQuantity,
    updateCartLine,
    incrementLineQuantity,
    decrementLineQuantity,
    removeLineItem,
    scheduleOrderDiscountFromUi: markDiscountDirtyFromUi,
    clearOrderDiscountOnServer,
    applyImmediateOps,
    enqueueSetCustomerAndFlush,
    flushNow,
    whenIdle,
    snapshotRevisionForSuspend
  }
}
